/**
 * Procedural Web Audio API BGM Engine for "Tembak Monyetnya!"
 * 
 * LICENSE & LEGAL:
 * - 100% Free, Open Source (MIT / CC0 compatible).
 * - 100% Legally Safe for Commercial Use (TikTok Mini Games, YouTube, Twitch, Reels, Ads).
 * - 0-byte external MP3/WAV download: Entire soundtrack is synthesized programmatically
 *   in real-time within the browser using Web Audio API oscillators and filters.
 * - Zero Content ID risk, zero copyright strikes, zero royalty fees.
 * 
 * MUSICAL STYLE:
 * - Tempo: 74 BPM ("Ngeslow", chill tropical lounge, relaxed arcade vibe).
 * - Key: C Major / F Lydian Pentatonic.
 * - Instruments:
 *   - Soft Marimba / Kalimba (Sine + Triangle overtone with lowpass warmth)
 *   - Gentle Arpeggio Chord Pad (Soft filtered warm triangle waves)
 *   - Mellow Acoustic Sub-Bass (Deep rounded low-end groove)
 *   - Lo-Fi Brushed Shaker, Soft Woodblock Rim Tap, and Pillowy Kick
 */

let audioCtx: AudioContext | null = null;
let masterGain: GainNode | null = null;
let isPlaying = false;
let isEnabled = true;
let currentMode: "menu" | "playing" | "cleared" | "lost" | "won" = "menu";

// Scheduler variables (Lookahead timing pattern)
let nextStepTime = 0;
let currentStep = 0;
let timerId: number | null = null;

// Tempo constants
const BPM = 74; // Slow, relaxed tropical arcade tempo
const SECONDS_PER_BEAT = 60 / BPM; // ~0.8108 seconds per quarter note
const STEP_TIME = SECONDS_PER_BEAT / 4; // 16th note step (~0.2027 seconds)
const LOOKAHEAD_MS = 40; // Frequency of scheduler tick (ms)
const SCHEDULE_AHEAD_TIME = 0.15; // Seconds to schedule ahead

// Musical Frequencies (Hz)
const N = {
  _: 0, // Rest

  // Bass octave (1-2)
  F1: 43.65,
  G1: 49.0,
  A1: 55.0,
  C2: 65.41,
  D2: 73.42,
  E2: 82.41,
  F2: 87.31,
  G2: 98.0,
  A2: 110.0,
  B2: 123.47,

  // Harmony / Mid octave (3)
  C3: 130.81,
  D3: 146.83,
  E3: 164.81,
  F3: 174.61,
  G3: 196.0,
  A3: 220.0,
  B3: 246.94,

  // Melody octave (4-5) - Sweet Marimba / Kalimba range
  C4: 261.63,
  D4: 293.66,
  E4: 329.63,
  F4: 349.23,
  G4: 392.0,
  A4: 440.0,
  B4: 493.88,
  C5: 523.25,
  D5: 587.33,
  E5: 659.25,
  G5: 783.99,
  A5: 880.0,
};

// 64-step composition (4 bars @ 74 BPM = ~13 seconds seamless loop)
// Bar 1 (0-15): Fmaj7 (Warm, relaxing tropical morning)
// Bar 2 (16-31): Cmaj7 (Bright, sunny, cozy jungle canopy)
// Bar 3 (32-47): Dm7 (Gentle laid-back swing)
// Bar 4 (48-63): G7sus -> C (Satisfying mellow resolution)

const BASS_PATTERN = [
  // Bar 1: F
  N.F2, N._, N._, N._, N.C2, N._, N.F2, N._,
  N.A2, N._, N._, N.C2, N._, N.F2, N._, N._,

  // Bar 2: C
  N.C2, N._, N._, N._, N.G1, N._, N.C2, N._,
  N.E2, N._, N._, N.G1, N._, N.C2, N._, N._,

  // Bar 3: Dm
  N.D2, N._, N._, N._, N.A1, N._, N.D2, N._,
  N.F2, N._, N._, N.A1, N._, N.D2, N._, N._,

  // Bar 4: G -> C
  N.G1, N._, N._, N._, N.D2, N._, N.G1, N._,
  N.G1, N._, N.D2, N._, N.B2, N._, N.C2, N._,
];

// Soft Arpeggiated Chords (Warm pad/marimba harmony)
const CHORD_PATTERN: Array<[number, number] | null> = [
  // Bar 1 (Fmaj7: F3, A3, C4, E4)
  [N.A3, N.C4], null, [N.C4, N.E4], null,
  [N.F3, N.A3], null, [N.A3, N.C4], null,
  [N.C4, N.E4], null, [N.A3, N.C4], null,
  [N.F3, N.A3], null, [N.A3, N.C4], null,

  // Bar 2 (Cmaj7: C3, E3, G3, B3)
  [N.G3, N.B3], null, [N.E3, N.G3], null,
  [N.C3, N.E3], null, [N.G3, N.C4], null,
  [N.E3, N.G3], null, [N.G3, N.B3], null,
  [N.C3, N.E3], null, [N.E3, N.G3], null,

  // Bar 3 (Dm7: D3, F3, A3, C4)
  [N.F3, N.A3], null, [N.A3, N.C4], null,
  [N.D3, N.F3], null, [N.F3, N.A3], null,
  [N.A3, N.C4], null, [N.F3, N.A3], null,
  [N.D3, N.F3], null, [N.F3, N.A3], null,

  // Bar 4 (G7sus / G / C: G3, C4, D4, F4 -> C3, E3, G3)
  [N.G3, N.C4], null, [N.D4, N.F4], null,
  [N.G3, N.B3], null, [N.D4, N.G4], null,
  [N.E3, N.G3], null, [N.G3, N.C4], null,
  [N.C3, N.E3], null, [N.E3, N.G3], null,
];

// Marimba / Kalimba Melody Pattern (Sweet, playful, slow & melodic)
const MELODY_PATTERN = [
  // Bar 1
  N.C5, N._, N.A4, N._, N.F4, N._, N.G4, N.A4,
  N._, N.C5, N._, N.D5, N.C5, N._, N.A4, N._,

  // Bar 2
  N.G4, N._, N.E4, N._, N.D4, N._, N.E4, N.G4,
  N._, N.A4, N._, N.G4, N.E4, N._, N._, N._,

  // Bar 3
  N.F4, N._, N.A4, N._, N.C5, N._, N.D5, N._,
  N.E5, N._, N.D5, N.C5, N.A4, N._, N.C5, N._,

  // Bar 4
  N.D5, N._, N.C5, N._, N.A4, N.G4, N.E4, N._,
  N.D4, N._, N.C4, N._, N.C4, N._, N._, N._,
];

// Percussion pattern:
// 0 = Rest
// 1 = Soft pillowy kick
// 2 = Gentle woodblock rim tap
// 3 = Soft brushed shaker
// 4 = Kick + Shaker
// 5 = Woodblock + Shaker
const DRUM_PATTERN = [
  // Bar 1
  4, 3, 3, 3, 5, 3, 3, 3,
  4, 3, 3, 3, 5, 3, 3, 3,

  // Bar 2
  4, 3, 3, 3, 5, 3, 3, 3,
  4, 3, 3, 3, 5, 3, 2, 3,

  // Bar 3
  4, 3, 3, 3, 5, 3, 3, 3,
  4, 3, 3, 3, 5, 3, 3, 3,

  // Bar 4
  4, 3, 3, 3, 5, 3, 3, 3,
  4, 3, 4, 3, 5, 2, 3, 0,
];

// -------------------------------------------------------------
// Audio Context & Master Gain Initialization
// -------------------------------------------------------------

function getAudioContext(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (!audioCtx) {
    const AudioContextClass =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (AudioContextClass) {
      audioCtx = new AudioContextClass();
    }
  }

  if (audioCtx && !masterGain) {
    masterGain = audioCtx.createGain();
    // Default master BGM volume: gentle background ambient level (~0.12)
    masterGain.gain.setValueAtTime(0.12, audioCtx.currentTime);
    masterGain.connect(audioCtx.destination);
  }

  return audioCtx;
}

// -------------------------------------------------------------
// Instrument Synthesizers
// -------------------------------------------------------------

/**
 * Mellow Acoustic Bass: Sine + subtle triangle overtone with lowpass warmth
 */
function playBass(ctx: AudioContext, dest: GainNode, freq: number, time: number) {
  if (!freq) return;

  const osc = ctx.createOscillator();
  const filter = ctx.createBiquadFilter();
  const noteGain = ctx.createGain();

  osc.type = "triangle";
  osc.frequency.setValueAtTime(freq, time);

  filter.type = "lowpass";
  filter.frequency.setValueAtTime(220, time);
  filter.Q.setValueAtTime(1.2, time);

  // Soft attack, smooth sustained decay
  noteGain.gain.setValueAtTime(0.001, time);
  noteGain.gain.linearRampToValueAtTime(0.45, time + 0.03);
  noteGain.gain.exponentialRampToValueAtTime(0.001, time + STEP_TIME * 1.8);

  osc.connect(filter);
  filter.connect(noteGain);
  noteGain.connect(dest);

  osc.start(time);
  osc.stop(time + STEP_TIME * 1.85);
}

/**
 * Sweet Marimba / Kalimba: Sine fundamental + soft octave overtone
 */
function playMarimba(ctx: AudioContext, dest: GainNode, freq: number, time: number) {
  if (!freq) return;

  // Fundamental osc (Sine)
  const osc1 = ctx.createOscillator();
  osc1.type = "sine";
  osc1.frequency.setValueAtTime(freq, time);

  // Harmonic overtone (Triangle, +1 octave for wooden bar ping)
  const osc2 = ctx.createOscillator();
  osc2.type = "triangle";
  osc2.frequency.setValueAtTime(freq * 2, time);

  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.setValueAtTime(1600, time);
  filter.frequency.exponentialRampToValueAtTime(600, time + 0.3);

  const noteGain = ctx.createGain();
  const duration = STEP_TIME * 1.6;

  noteGain.gain.setValueAtTime(0.001, time);
  noteGain.gain.linearRampToValueAtTime(0.28, time + 0.006);
  noteGain.gain.exponentialRampToValueAtTime(0.001, time + duration);

  const gain2 = ctx.createGain();
  gain2.gain.setValueAtTime(0.18, time);

  osc1.connect(filter);
  osc2.connect(gain2);
  gain2.connect(filter);
  filter.connect(noteGain);
  noteGain.connect(dest);

  osc1.start(time);
  osc2.start(time);
  osc1.stop(time + duration + 0.02);
  osc2.stop(time + duration + 0.02);
}

/**
 * Warm Pad / Arpeggio Chords: Filtered triangle chord tones
 */
function playChordNote(ctx: AudioContext, dest: GainNode, freq: number, time: number) {
  if (!freq) return;

  const osc = ctx.createOscillator();
  const filter = ctx.createBiquadFilter();
  const noteGain = ctx.createGain();
  const duration = STEP_TIME * 1.9;

  osc.type = "triangle";
  osc.frequency.setValueAtTime(freq, time);

  filter.type = "lowpass";
  filter.frequency.setValueAtTime(800, time);

  noteGain.gain.setValueAtTime(0.001, time);
  noteGain.gain.linearRampToValueAtTime(0.12, time + 0.04);
  noteGain.gain.exponentialRampToValueAtTime(0.001, time + duration);

  osc.connect(filter);
  filter.connect(noteGain);
  noteGain.connect(dest);

  osc.start(time);
  osc.stop(time + duration + 0.02);
}

/**
 * Soft Pillowy Kick: Gentle rounded low-end pulse (not harsh or boomy)
 */
function playPillowyKick(ctx: AudioContext, dest: GainNode, time: number) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();

  osc.type = "sine";
  osc.frequency.setValueAtTime(86, time);
  osc.frequency.exponentialRampToValueAtTime(36, time + 0.11);

  gain.gain.setValueAtTime(0.28, time);
  gain.gain.exponentialRampToValueAtTime(0.001, time + 0.12);

  osc.connect(gain);
  gain.connect(dest);

  osc.start(time);
  osc.stop(time + 0.13);
}

/**
 * Gentle Woodblock / Rim Tap: Acoustic hollow click
 */
function playWoodblock(ctx: AudioContext, dest: GainNode, time: number) {
  const osc = ctx.createOscillator();
  const filter = ctx.createBiquadFilter();
  const gain = ctx.createGain();

  osc.type = "triangle";
  osc.frequency.setValueAtTime(560, time);
  osc.frequency.exponentialRampToValueAtTime(240, time + 0.04);

  filter.type = "bandpass";
  filter.frequency.setValueAtTime(420, time);
  filter.Q.setValueAtTime(2.0, time);

  gain.gain.setValueAtTime(0.16, time);
  gain.gain.exponentialRampToValueAtTime(0.001, time + 0.045);

  osc.connect(filter);
  filter.connect(gain);
  gain.connect(dest);

  osc.start(time);
  osc.stop(time + 0.05);
}

/**
 * Brushed Shaker: Delicate high-frequency whispered pulse
 */
function playBrushedShaker(ctx: AudioContext, dest: GainNode, time: number) {
  const bufferSize = Math.floor(ctx.sampleRate * 0.035);
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);

  for (let i = 0; i < bufferSize; i++) {
    data[i] = Math.random() * 2 - 1;
  }

  const noise = ctx.createBufferSource();
  noise.buffer = buffer;

  const filter = ctx.createBiquadFilter();
  filter.type = "highpass";
  filter.frequency.setValueAtTime(6500, time);

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.045, time);
  gain.gain.exponentialRampToValueAtTime(0.001, time + 0.032);

  noise.connect(filter);
  filter.connect(gain);
  gain.connect(dest);

  noise.start(time);
  noise.stop(time + 0.035);
}

// -------------------------------------------------------------
// Lookahead Scheduler
// -------------------------------------------------------------

function scheduleStep(stepIndex: number, time: number, ctx: AudioContext, dest: GainNode) {
  const idx = stepIndex % 64;

  // 1. Bass
  const bassFreq = BASS_PATTERN[idx];
  if (bassFreq) {
    playBass(ctx, dest, bassFreq, time);
  }

  // 2. Chords
  const chord = CHORD_PATTERN[idx];
  if (chord) {
    playChordNote(ctx, dest, chord[0], time);
    playChordNote(ctx, dest, chord[1], time);
  }

  // 3. Melody (Marimba)
  const melodyFreq = MELODY_PATTERN[idx];
  if (melodyFreq) {
    playMarimba(ctx, dest, melodyFreq, time);
  }

  // 4. Percussion
  const drum = DRUM_PATTERN[idx];
  if (drum === 1 || drum === 4) {
    playPillowyKick(ctx, dest, time);
  }
  if (drum === 2 || drum === 5) {
    playWoodblock(ctx, dest, time);
  }
  if (drum === 3 || drum === 4 || drum === 5) {
    playBrushedShaker(ctx, dest, time);
  }
}

function schedulerTick() {
  if (!isPlaying || !isEnabled) return;
  const ctx = getAudioContext();
  if (!ctx || !masterGain) return;

  // Schedule all steps that fall within the lookahead window
  while (nextStepTime < ctx.currentTime + SCHEDULE_AHEAD_TIME) {
    scheduleStep(currentStep, nextStepTime, ctx, masterGain);
    nextStepTime += STEP_TIME;
    currentStep++;
  }

  timerId = window.setTimeout(schedulerTick, LOOKAHEAD_MS);
}

// -------------------------------------------------------------
// Public API
// -------------------------------------------------------------

/**
 * Start the chill procedural slow BGM.
 * Safe to call repeatedly; starts audio context if user has interacted.
 */
export function startTembakMonyetBgm() {
  if (isPlaying) return;
  const ctx = getAudioContext();
  if (!ctx || !masterGain) return;

  if (ctx.state === "suspended") {
    void ctx.resume();
  }

  isPlaying = true;
  nextStepTime = ctx.currentTime + 0.05;
  currentStep = 0;

  // Gentle fade-in
  masterGain.gain.setValueAtTime(0.001, ctx.currentTime);
  masterGain.gain.linearRampToValueAtTime(isEnabled ? 0.12 : 0.001, ctx.currentTime + 0.4);

  schedulerTick();
}

/**
 * Stop or pause the BGM with a smooth fade-out to prevent clicks.
 */
export function stopTembakMonyetBgm() {
  if (!isPlaying) return;
  isPlaying = false;

  if (timerId !== null) {
    clearTimeout(timerId);
    timerId = null;
  }

  if (masterGain && audioCtx) {
    try {
      masterGain.gain.linearRampToValueAtTime(0.001, audioCtx.currentTime + 0.15);
    } catch {
      // Ignore if context is closed
    }
  }
}

/**
 * Toggle or set enabled state (soundOn toggle).
 */
export function setTembakMonyetBgmEnabled(enabled: boolean) {
  isEnabled = enabled;
  if (!audioCtx || !masterGain) return;

  const now = audioCtx.currentTime;
  if (enabled) {
    if (audioCtx.state === "suspended") {
      void audioCtx.resume();
    }
    masterGain.gain.cancelScheduledValues(now);
    masterGain.gain.setValueAtTime(masterGain.gain.value, now);
    masterGain.gain.linearRampToValueAtTime(0.12, now + 0.3);
    if (!isPlaying) {
      startTembakMonyetBgm();
    }
  } else {
    masterGain.gain.cancelScheduledValues(now);
    masterGain.gain.setValueAtTime(masterGain.gain.value, now);
    masterGain.gain.linearRampToValueAtTime(0.0001, now + 0.2);
  }
}

/**
 * Adapt BGM mood or volume depending on game mode.
 * In "lost" or "won" screens, duck or pause the BGM gently.
 */
export function setTembakMonyetBgmMode(mode: "menu" | "playing" | "cleared" | "lost" | "won") {
  currentMode = mode;
  if (!audioCtx || !masterGain || !isEnabled) return;

  const now = audioCtx.currentTime;
  masterGain.gain.cancelScheduledValues(now);
  masterGain.gain.setValueAtTime(masterGain.gain.value, now);

  if (currentMode === "lost") {
    // Softly duck during game over
    masterGain.gain.linearRampToValueAtTime(0.03, now + 0.3);
  } else if (currentMode === "won") {
    // Soft celebration level
    masterGain.gain.linearRampToValueAtTime(0.08, now + 0.3);
  } else {
    // Standard chill lounge volume
    masterGain.gain.linearRampToValueAtTime(0.12, now + 0.3);
  }
}

export function isTembakMonyetBgmPlaying(): boolean {
  return isPlaying;
}
